import { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Warning } from '@mui/icons-material';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import { type ProjectList, useListProjects } from '@api/project';
import AppDataGrid, {
  GridCountCell,
  GridPersonCell,
  GridPrimaryText,
  GridRowActions,
  GridSecondaryText,
} from '@components/DataGrid';
import CustomNoRowsOverlay from '@components/CustomNoRowsOverlay';
import CustomDialog from '@components/Dialog/CustomDialog';
import { QUERY_KEYSX, useDeleteProject } from '@/api/project/project';

const NoProjectsOverlay = () => (
  <CustomNoRowsOverlay message="No projects yet" />
);

const ProjectsList = () => {
  const { data } = useListProjects();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const deleteProject = useDeleteProject();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const onSubmitDialog = async () => {
    if (deleteId === null) {
      enqueueSnackbar('Failed to delete project', {
        variant: 'error',
      });
    } else {
      try {
        await deleteProject
          .mutateAsync(deleteId)
          .then(() => {
            enqueueSnackbar('Project delete successfully', {
              variant: 'success',
            });
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYSX.PROJECTS,
              exact: true,
            });
          })
          .catch(() => {
            enqueueSnackbar('Failed to delete project', {
              variant: 'error',
            });
          });
      } catch (ex) {
        enqueueSnackbar('Error. Please contact admin', {
          variant: 'error',
        });
      }
    }
    handleDialogClose();
  };

  const columns = useMemo<GridColDef<ProjectList>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.1,
        minWidth: 160,
        renderCell: (params) => (
          <GridPrimaryText>{params.value}</GridPrimaryText>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.4,
        minWidth: 180,
        renderCell: (params) => <GridSecondaryText value={params.value} />,
      },
      {
        field: 'experiment_group_count',
        headerName: 'Experiments',
        flex: 0.6,
        minWidth: 130,
        renderCell: (params) => <GridCountCell value={params.value} />,
      },
      {
        headerName: 'Created by',
        field: 'created_by',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => {
          const { first_name: firstName, last_name: lastName } =
            params.row.created_by || {};
          return <GridPersonCell firstName={firstName} lastName={lastName} />;
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        flex: 0.8,
        minWidth: 168,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <GridRowActions
            onEdit={() => navigate(`/projects/${params.row.id}`)}
            onDelete={() => {
              setDeleteId(params.row.id);
              setDialogOpen(true);
            }}
          />
        ),
      },
    ],
    [navigate],
  );

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.25,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="h3">Project registry</Typography>
            <Typography variant="body2" color="text.secondary">
              Select a project to view its experiment groups and samples.
            </Typography>
          </Stack>
          <Chip
            label={`${data?.length ?? 0} project${
              data?.length === 1 ? '' : 's'
            }`}
            size="small"
            sx={{ bgcolor: 'primary.light', color: 'primary.main' }}
          />
        </Box>
        <AppDataGrid
          rows={data}
          columns={columns}
          checkboxSelection
          onRowClick={(params) => {
            navigate(`/projects/${params.row.id}`);
          }}
          slots={{
            noRowsOverlay: NoProjectsOverlay,
          }}
          sx={{ border: 0, borderRadius: 0 }}
        />
      </Paper>
      <CustomDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <span>Delete Project</span>
          </Stack>
        }
        onSubmit={onSubmitDialog}
      >
        <div>Are you sure you want to delete project?</div>
      </CustomDialog>
    </>
  );
};

export default ProjectsList;
